import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import DetailLogList from "./DetailLogList";
import { CheckInCheckOutData } from "./DonutChartStatus";

interface Stats {
  burnout: number;
  good: number;
  under: number;
  none: number;
  totalWorkHours: number;
  totalWorkDays: number;
}

interface Props {
  stats: Stats;
  entries: CheckInCheckOutData[];
  mode?: "month" | "year" | "week" | "overall";
}

const COLORS = ["#F56565", "#48BB78", "#ECC94B", "#CBD5E0"];
const NoopActiveShape = () => <g />;

const MonthlyBurnoutChart: React.FC<Props> = ({ stats, entries, mode }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const data = [
    { name: "Burnout Days", value: stats.burnout },
    { name: "Good Days", value: stats.good },
    { name: "Underworking Days", value: stats.under },
    { name: "No Work Days", value: stats.none },
  ];

  return (
    <Box mt={1} borderRadius="md" px={6} bg="zs.card" borderWidth="1px" borderColor="zs.border">
      <Tabs
        index={tabIndex}
        onChange={(index) => setTabIndex(index)}
        variant="enclosed"
      >
        {/* Top Header Row with Tabs Aligned Right */}
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontSize="md" fontWeight="bold" color="zs.text">
             Overview Analysis
          </Text>
          <TabList>
            <Tab fontSize="sm">Summary</Tab>
            <Tab fontSize="sm">Detail Logs</Tab>
          </TabList>
        </Flex>

        <TabPanels>
          {/* 🔹 Tab 0 - Summary View */}
          <TabPanel px={0}>
            <Flex wrap="wrap" justify="space-between" gap={6}>
              {/* Left - Pie Chart */}
              <Box flex="1" minW="350px" border="none" boxShadow="none">
                <PieChart width={280} height={300} style={{ outline: "none" }}>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    fontSize={"xs"}
                    outerRadius={80}
                    label={({ percent }) => `${(percent * 100).toFixed(1)}%`}
                    isAnimationActive
                    activeIndex={-1}
                    activeShape={() => <></>}
                    stroke="none"
                  >
                    {data.map((_, i) => (
                      <Cell
                        key={`cell-${i}`}
                        fill={COLORS[i]}
                        stroke="none"
                        fontSize={"14px"}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend fontSize={"sm"} />
                </PieChart>
              </Box>

              {/* Right - Summary Panel */}
              <Box flex="1" minW="280px" px={4} py={2}>
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  mb={3}
                  color="zs.text"
                >
                  Summary
                </Text>

                <VStack align="stretch" spacing={2}>
                  <Flex justify="space-between" fontSize="sm" align="center">
                    <Flex align="center" gap={2}>
                      <Box as="span" w="2" h="2" borderRadius="full" bg="red.400" />
                      <Text color="zs.text">Burnout</Text>
                    </Flex>
                    <Text color="red.500" fontWeight="medium">
                      {stats.burnout}
                    </Text>
                  </Flex>

                  <Flex justify="space-between" fontSize="sm" align="center">
                    <Flex align="center" gap={2}>
                      <Box as="span" w="2" h="2" borderRadius="full" bg="green.400" />
                      <Text color="zs.text">Good</Text>
                    </Flex>
                    <Text color="green.500" fontWeight="medium">
                      {stats.good}
                    </Text>
                  </Flex>

                  <Flex justify="space-between" fontSize="sm" align="center">
                    <Flex align="center" gap={2}>
                      <Box as="span" w="2" h="2" borderRadius="full" bg="yellow.400" />
                      <Text color="zs.text">Underworking</Text>
                    </Flex>
                    <Text color="yellow.600" fontWeight="medium">
                      {stats.under}
                    </Text>
                  </Flex>

                  {mode !== "overall" && (
                    <Flex justify="space-between" fontSize="sm" align="center">
                      <Flex align="center" gap={2}>
                        <Box as="span" w="2" h="2" borderRadius="full" bg="gray.400" />
                        <Text color="zs.text">No Work</Text>
                      </Flex>
                      <Text color="gray.600" fontWeight="medium">
                        {stats.none}
                      </Text>
                    </Flex>
                  )}

                  <Flex justify="space-between" fontSize="sm" align="center">
                    <Text color="zs.text">Work Days</Text>
                    <Text color="blue.500" fontWeight="medium">
                      {stats.totalWorkDays}
                    </Text>
                  </Flex>

                  <Flex
                    justify="space-between"
                    fontSize="sm"
                    pt={2}
                    borderTop="1px solid"
                    borderColor="zs.border"
                    mt={2}
                  >
                    <Text color="zs.text" fontWeight="semibold">
                      Total Hours
                    </Text>
                    <Text fontWeight="bold" color="blue.300">
                      {stats.totalWorkHours.toFixed(1)} hrs
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            </Flex>
          </TabPanel>

          {/* View Details Button */}

          {/* Collapsible Detailed Logs */}
          <TabPanel px={0}>
            <DetailLogList entries={entries} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default MonthlyBurnoutChart;
